import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import "./ColorSelector.css"
import Icon from "../base/Icon"
import { BottomSheet } from "../base/BottomSheet"
import ToolTip from "../base/ToolTip"
import { loadJobColors, useGlobal, useHub, useScreen } from "../../core/Global"
import { canManageColors, isSameColor, useColorName, usePalette } from "./ColorPalette"
import ColorPad from "./ColorPad"
import { API_ROUTES } from "../../core/Constants"

interface IProps {
    /** Panel heading, e.g. "Job Color". Also drives the trigger's tooltip. */
    title?: string
    /** Second line on the mobile sheet, e.g. the record this colour belongs to. */
    subtitle?: string
    /** Currently selected colour; undefined means "no colour". */
    color?: string
    /** Called with the picked colour, or undefined when "No Color" is chosen. */
    onSelect: (color?: string) => void
    /** Distinguishes the mobile sheet from other open sheets. */
    id: string
    /**
     * "icon" (default) is the droplet button that lives in a toolbar.
     * "spine" is a full-height band on the left edge of a list row, which doubles as the trigger.
     * "bar" is a band across the top edge of a panel (e.g. a drawer), likewise the trigger.
     * "field" looks like a form input (swatch + colour name), for settings forms.
     */
    variant?: "icon" | "spine" | "bar" | "field"
    /** "field" only: the text shown when there's no colour. */
    placeholder?: string
    /** Icon size of the droplet trigger. Ignored by the spine. */
    size?: number
    className?: string
    /** Keep the droplet fully visible even without a colour (it dims on hover-only rows by default). */
    alwaysVisible?: boolean
    /** Shows a spinner on the droplet while the caller persists the choice. */
    saving?: boolean
}

const PANEL_WIDTH = 226
const PANEL_HEIGHT = 140
const PANEL_HEIGHT_PICKING = 360

interface PaletteGridProps {
    color?: string
    canAdd: boolean
    picking: boolean
    onPick: (color?: string) => void
    onTogglePicking: () => void
}

/**
 * The swatch grid, split out so it — and only it — subscribes to the store.
 *
 * The palette is fetched the first time a picker opens (and an account's defaults are seeded by
 * that same request), so on a fresh account the response lands *after* the panel has rendered.
 * ColorSelector itself must NOT subscribe: a list renders one per row, and they'd all repaint on
 * every unrelated store change. This grid only exists while a panel is open, so at most one is
 * ever subscribed.
 */
function PaletteGrid(props: PaletteGridProps) {

    useGlobal()
    const palette = usePalette()

    return (
        <div className="color-selector-grid">

            <ToolTip content="No Color" placement="top">
                <button type="button"
                    className={`color-selector-swatch is-none${props.color ? "" : " selected"}`}
                    onClick={e => {
                        e.preventDefault()
                        e.stopPropagation()
                        props.onPick(undefined)
                    }} />
            </ToolTip>

            {
                palette.map(c =>
                    // The account's own name for the colour, on the project ToolTip so it appears
                    // the instant the pointer arrives instead of after the browser's ~1s delay.
                    <ToolTip key={c.value} content={c.name} placement="top">
                        <button type="button"
                            className={`color-selector-swatch${isSameColor(props.color, c.value) ? " selected" : ""}`}
                            style={{ backgroundColor: c.value, ["--swatch" as any]: c.value }}
                            onClick={e => {
                                e.preventDefault()
                                e.stopPropagation()
                                props.onPick(c.value)
                            }}>
                            {
                                isSameColor(props.color, c.value) &&
                                <Icon icon="check" type="solid" size={11} />
                            }
                        </button>
                    </ToolTip>
                )
            }

            {
                props.canAdd &&
                <ToolTip content={props.picking ? "Close" : "Add Color"} placement="top">
                    <button type="button"
                        className={`color-selector-swatch is-add${props.picking ? " open" : ""}`}
                        onClick={e => {
                            e.preventDefault()
                            e.stopPropagation()
                            props.onTogglePicking()
                        }}>
                        <Icon icon={props.picking ? "minus" : "plus"} type="solid" size={11} />
                    </button>
                </ToolTip>
            }

        </div>
    )
}

/**
 * The console's colour picker: a trigger plus a palette popover (bottom sheet on mobile).
 *
 * It owns no persistence for the *selection* — the caller decides what a colour means and where it
 * goes, and just reacts to onSelect. The palette itself comes from the account (defaults + custom
 * colours); the "+" cell is a native colour input, so admins pick any colour straight from the
 * browser's own picker and it is both applied to the record and saved to the account palette.
 */
export default function ColorSelector(props: IProps) {

    const hub = useHub()
    const screen = useScreen()
    const paletteName = useColorName(props.color)

    const triggerRef = useRef<HTMLButtonElement>(null)
    const panelRef = useRef<HTMLDivElement>(null)

    const [open, openSet] = useState<boolean>(false)
    // The "+" cell expands the panel with a colour pad instead of committing anything on its own.
    const [picking, pickingSet] = useState<boolean>(false)
    const [position, positionSet] = useState<{ top: number, left: number }>({ top: 0, left: 0 })

    // The mobile sheet is mounted only while it's in use: BottomSheet registers itself in the
    // global component stack (the native back button / app-close guard), and a list renders one
    // selector per row — keeping them all mounted would flood that stack.
    const [sheetMounted, sheetMountedSet] = useState<boolean>(false)

    useEffect(() => {
        if (open) {
            // Fetched on first open rather than on mount: a list renders one selector per row, and
            // only the open panel actually needs the palette. loadJobColors no-ops once loaded.
            loadJobColors()
            sheetMountedSet(true)
            return
        }
        pickingSet(false)
        // Stay mounted just long enough for the sheet's own slide-down animation.
        const t = setTimeout(() => sheetMountedSet(false), 260)
        return () => clearTimeout(t)
    }, [open])

    // Re-anchor when the pad expands the panel, so a panel near the bottom flips up instead of
    // growing off-screen.
    useLayoutEffect(() => {
        if (open && !screen.isMobile) placePanel()
    }, [open, picking])

    useEffect(() => {
        if (!open || screen.isMobile) return

        const onOutside = (e: MouseEvent) => {
            const t = e.target as Node
            if (panelRef.current?.contains(t) || triggerRef.current?.contains(t)) return
            openSet(false)
        }
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") openSet(false)
        }
        // Lists are usually virtualized, so the anchor moves with the scroller — just close.
        const onMoved = () => openSet(false)

        document.addEventListener("mousedown", onOutside)
        document.addEventListener("keydown", onKey)
        window.addEventListener("resize", onMoved)
        window.addEventListener("scroll", onMoved, true)

        return () => {
            document.removeEventListener("mousedown", onOutside)
            document.removeEventListener("keydown", onKey)
            window.removeEventListener("resize", onMoved)
            window.removeEventListener("scroll", onMoved, true)
        }
    }, [open, screen.isMobile])


    function placePanel() {
        const rect = triggerRef.current?.getBoundingClientRect()
        if (!rect) return

        // The spine is a full-height band, so anchoring below it would drop the panel a whole row
        // away — it pops out beside the band instead. The droplet anchors under itself.
        const spine = props.variant === "spine"

        // The field anchors under its left edge, like a dropdown's list.
        let left = spine ? rect.right + 8 : props.variant === "field" ? rect.left : rect.left + rect.width / 2 - PANEL_WIDTH / 2
        left = Math.min(Math.max(8, left), window.innerWidth - PANEL_WIDTH - 8)

        const height = picking ? PANEL_HEIGHT_PICKING : PANEL_HEIGHT
        const wanted = spine ? rect.top : rect.bottom + 8
        const top = wanted + height > window.innerHeight
            ? Math.max(8, window.innerHeight - height - 8)
            : wanted

        positionSet({ top, left })
    }

    const title = props.title ?? "Color"
    const canAdd = canManageColors()

    const pick = (value?: string) => {
        openSet(false)
        props.onSelect(value)
    }

    /** Applies a brand new colour to the record AND adds it to the account palette in one go. */
    const addAndPick = (hex: string) => {
        pick(hex)

        // Named by its hex to begin with; CRM > Base Data > Job Colors is where it gets a real name.
        hub.Post(API_ROUTES.JobColors, { name: hex, color: hex }, res => {
            if (res.success) loadJobColors(true)
        })
    }

    const swatches = () => (
        <PaletteGrid color={props.color} canAdd={canAdd} picking={picking}
            onPick={pick} onTogglePicking={() => pickingSet(p => !p)} />
    )

    /** The pad expands the panel right under the grid — i.e. directly below the "+" cell. */
    const pad = () => (
        <ColorPad initial={props.color}
            onCancel={() => pickingSet(false)}
            onUse={hex => {
                pickingSet(false)
                addAndPick(hex)
            }} />
    )

    const toggle = (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        openSet(o => !o)
    }

    const triggerTitle = props.color ? `${title}: ${paletteName}` : title

    return (
        <>
            {
                props.variant === "field" ?
                    <button type="button" ref={triggerRef}
                        className={`color-selector-field form-control${open ? " open" : ""} ${props.className ?? ""}`}
                        onClick={toggle}>
                        {
                            props.color
                                ? <span className="color-selector-field-swatch" style={{ backgroundColor: props.color }} />
                                : <span className="color-selector-field-swatch is-none" />
                        }
                        <span className={props.color ? "text-gray-800" : "text-gray-500"}>
                            {props.color ? paletteName : (props.placeholder ?? "No color")}
                        </span>
                        {props.saving && <Icon icon="circle-notch" type="solid" spin size={11} className="ms-2" />}
                        <Icon icon={open ? "chevron-up" : "chevron-down"} size={10} className="ms-auto text-gray-500" />
                    </button>
                    :
                props.variant === "spine" || props.variant === "bar" ?
                    // The wrapper carries the band's geometry so the tooltip can measure a real
                    // box; the button just fills it (see .color-selector-band-tip).
                    <ToolTip content={triggerTitle} className={`color-selector-band-tip is-${props.variant}`}
                        placement={props.variant === "spine" ? "right" : "bottom"}>
                        <button type="button" ref={triggerRef}
                            className={`color-selector-${props.variant}${props.color ? "" : " is-empty"} ${props.className ?? ""}`}
                            // Explicit rather than inherited, so clearing the colour empties the
                            // band immediately instead of waiting for the container to repaint.
                            style={{ backgroundColor: props.color }}
                            onClick={toggle} />
                    </ToolTip>
                    :
                    <ToolTip content={triggerTitle} placement="top">
                        <button type="button" ref={triggerRef}
                            className={`color-selector-trigger${props.color ? "" : " is-empty"}${props.alwaysVisible ? " always-visible" : ""} ${props.className ?? ""}`}
                            onClick={toggle}>
                            {
                                props.saving ?
                                    <Icon icon="circle-notch" type="solid" spin size={props.size ?? 16} color={props.color} />
                                    :
                                    <Icon icon="droplet" type={props.color ? "solid" : "regular"} size={props.size ?? 16}
                                        color={props.color} className={props.color ? undefined : "text-dark"} />
                            }
                        </button>
                    </ToolTip>
            }

            {
                open && !screen.isMobile &&
                createPortal(
                    <div ref={panelRef} className="color-selector-panel"
                        style={{ top: position.top, left: position.left, width: PANEL_WIDTH }}
                        onClick={e => e.stopPropagation()}>
                        <div className="color-selector-title">{title}</div>
                        {swatches()}
                        {picking && pad()}
                    </div>,
                    document.body
                )
            }

            {
                screen.isMobile && sheetMounted &&
                <BottomSheet id={`color-selector-sheet-${props.id}`} open={open}
                    onClose={() => openSet(false)}>
                    <div className="color-selector-sheet">
                        <div className="text-center mb-5">
                            <div className="fs-4 fw-bolder text-gray-900">{title}</div>
                            {props.subtitle && <div className="fs-8 text-gray-500">{props.subtitle}</div>}
                        </div>
                        {swatches()}
                        {picking && pad()}
                    </div>
                </BottomSheet>
            }
        </>
    )
}
